import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function Pipeline() {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggedJob, setDraggedJob] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/pipeline/view`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pipeline');
      }

      const data = await response.json();
      setPipeline(data.pipeline);
      setStats(data.stats);
    } catch (error) {
      console.error('Error fetching pipeline:', error);
      alert('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, job) => {
    setDraggedJob(job);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(job));

    // Add dragging class for visual feedback
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('opacity-50');
    setDraggedJob(null);
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedJob) {
      return;
    }

    // Don't move if already in the same stage
    if (draggedJob.pipeline_stage_id === targetStageId) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/pipeline/jobs/${draggedJob.id}/stage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pipeline_stage_id: targetStageId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update job stage');
      }

      // Refresh the pipeline
      await fetchPipeline();
    } catch (error) {
      console.error('Error updating job stage:', error);
      alert(`Failed to move job: ${error.message}`);
    } finally {
      setDraggedJob(null);
      setIsDragging(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading pipeline...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Sales Pipeline</h1>
            <p className="text-gray-600 mt-1">Drag and drop jobs to move them between stages</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/joblist/jobs/create')}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              + New Job
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Jobs</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total_jobs}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Value</div>
              <div className="text-2xl font-bold text-emerald-600">
                {formatCurrency(stats.total_value)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Profit</div>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(stats.total_profit)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pipeline Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {pipeline.map((stage) => (
          <div
            key={stage.id}
            className="flex-shrink-0 w-80 bg-gray-100 rounded-lg p-4"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.id)}
          >
            {/* Stage Header */}
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: stage.color }}
                  />
                  <h2 className="font-semibold text-gray-900">{stage.display_name}</h2>
                </div>
                <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded">
                  {stage.jobs.length}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{stage.description}</p>

              {/* Stage stats */}
              {stats && (
                <div className="mt-2 text-xs text-gray-600">
                  {formatCurrency(
                    stats.by_stage.find(s => s.stage_id === stage.id)?.value || 0
                  )}
                </div>
              )}
            </div>

            {/* Job Cards */}
            <div className="space-y-3 min-h-[200px]">
              {stage.jobs.map((job) => (
                <div
                  key={job.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, job)}
                  onDragEnd={handleDragEnd}
                  onClick={(e) => {
                    // Don't navigate if we just finished dragging
                    if (!isDragging) {
                      navigate(`/joblist/jobs/${job.id}`);
                    }
                  }}
                  className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">
                        {job.job_name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {job.job_number}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-700 mb-2">
                    <div className="font-medium">{job.client_name}</div>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <div className="text-gray-600">
                      Value: <span className="font-semibold text-emerald-600">
                        {formatCurrency(Number(job.total_quote_out))}
                      </span>
                    </div>
                    <div className="text-gray-600">
                      Profit: <span className={`font-semibold ${Number(job.total_profit) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(Number(job.total_profit))}
                      </span>
                    </div>
                  </div>

                  {(job.account_manager || job.project_manager) && (
                    <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                      {job.account_manager && (
                        <div>AM: {job.account_manager}</div>
                      )}
                      {job.project_manager && (
                        <div>PM: {job.project_manager}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {stage.jobs.length === 0 && (
                <div className="text-center text-gray-400 text-sm py-8">
                  No jobs in this stage
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {pipeline.length === 0 && (
        <div className="text-center text-gray-500 py-12">
          <p className="text-lg">No pipeline stages configured</p>
        </div>
      )}
    </div>
  );
}

